// Turns an axios failure from POST /organization-questionnaires/{id}/ai-ingest into something a user can
// act on.
//
// It lives here rather than in `utils/index.js` because it is specific to one endpoint's error contract, and
// because the app-wide `showErrorToast` cannot be used for it at all: that helper unwraps
// `response.data.parameters.message`, and the ai-ingest 422 has no `parameters` — `ApiExceptionHandler`
// returns the raw `AiIngestResponseDto` so the per-row detail survives (a generic handler would collapse it
// to one flat message, destroying exactly the information the user needs to fix the mapping).
//
// Pure and dependency-free so it can be unit-tested without a browser: see `test/aiDataMapping/`.

// Only the first N row errors are worth rendering — a systematic mapping mistake produces one error per row,
// and 20,000 of them tell the user nothing that 25 do not. Java already caps its own list; this is the
// display cap on top of whatever arrives.
export const MAX_DISPLAYED_ROW_ERRORS = 25;

const GENERIC = {
  title: "Commit failed",
  description: "Something went wrong writing to the task. Please try again.",
};

// The 422 body is an AiIngestResponseDto, not an ErrorDto. `errors` being an array is the discriminator —
// checking the status alone is not enough, since a proxy or gateway can also answer 422.
const isRowErrorBody = (data) => Boolean(data) && Array.isArray(data.errors);

/**
 * `parameters.message` on an ErrorDto. For `ObjectArgumentNotValidException(List.of(...))` the value is a
 * JSON-encoded array of strings, because the handler runs the list through `writeValueAsString`; for
 * `PreconditionFailed` and the rest it is a plain sentence. Handle both, and fall back to the raw text
 * rather than showing the user a JSON parse failure.
 */
function readErrorDtoMessage(data) {
  const raw = data?.parameters?.message;
  if (typeof raw !== "string" || raw.trim() === "") return null;
  // "null" is what the String constructor of ObjectArgumentNotValidException produces (it never sets
  // `errors`). Treat it as absent rather than showing the word to a user.
  if (raw === "null") return null;
  if (!raw.startsWith("[")) return raw;
  try {
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) {
      const messages = parsed.filter((m) => typeof m === "string" && m.trim() !== "");
      return messages.length > 0 ? messages.join(" ") : null;
    }
  } catch {
    // Not JSON after all — a legitimate message may simply begin with a bracket.
  }
  return raw;
}

/**
 * @param {unknown} error an axios error (or anything else that reached the catch block)
 * @param {{ dryRun?: boolean }} options whether the failed call was a dry run, which changes the wording:
 *   a rejected dry run wrote nothing by design, and saying "nothing was written" about it would imply a
 *   commit had been attempted.
 * @returns {{ title: string, description: string, rowErrors: Array<object>, totalRowErrors: number,
 *   status: number|null, isValidationFailure: boolean }}
 */
export function describeIngestError(error, { dryRun = false } = {}) {
  const status = error?.response?.status ?? null;
  const data = error?.response?.data;
  const empty = { rowErrors: [], totalRowErrors: 0, status, isValidationFailure: false };

  // No response at all: the request never landed, or the browser blocked it. Distinguished from a 5xx
  // because "the server rejected this" and "the server was never reached" call for different next steps.
  if (!error?.response) {
    return {
      ...empty,
      title: "Could not reach the server",
      description:
        "The request did not complete. Check your connection and try again — nothing was written to the task.",
    };
  }

  if (status === 422 && isRowErrorBody(data)) {
    const rowErrors = data.errors;
    return {
      ...empty,
      isValidationFailure: true,
      rowErrors: rowErrors.slice(0, MAX_DISPLAYED_ROW_ERRORS),
      totalRowErrors: rowErrors.length,
      title: dryRun ? "Validation found problems" : "Rows were rejected",
      description:
        rowErrors.length === 1
          ? "One row does not fit the task's output structure. Nothing was written — ask the AI to correct it and re-check."
          : `${rowErrors.length.toLocaleString()} rows do not fit the task's output structure. Nothing was written — ask the AI to correct them and re-check.`,
    };
  }

  const message = readErrorDtoMessage(data);

  switch (status) {
    case 412:
      // Either the descriptor moved under the session (`templateVersion` mismatch) or someone else changed
      // the rows this commit meant to replace. Both are "your view is stale", and both are recoverable by
      // reloading rather than by editing the data.
      return {
        ...empty,
        title: "The task changed since you started",
        description:
          message ??
          "The task's rows or output structure changed while this session was open. Reload and re-check the mapping before committing.",
      };
    case 409:
      return {
        ...empty,
        title: "This task is finalised",
        description: message ?? "Unfinalise the task before committing data into it.",
      };
    case 400:
      return {
        ...empty,
        title: "The request was rejected",
        description: message ?? GENERIC.description,
      };
    case 401:
      return {
        ...empty,
        title: "Your session expired",
        description: "Sign in again, then re-check the mapping before committing.",
      };
    case 403:
      return {
        ...empty,
        title: "You cannot write to this task",
        description:
          message ?? "Your account does not have permission to add data to this task. Ask its controller.",
      };
    case 404:
      return {
        ...empty,
        title: "Task not found",
        description: message ?? "This task no longer exists, or is not visible to your organisation.",
      };
    default:
      return { ...empty, title: GENERIC.title, description: message ?? GENERIC.description };
  }
}

/**
 * A single row error as one line. `field` is a row-DTO path (`consumption.measureId`), not a source column —
 * mapping it back to the spreadsheet column it came from is ambiguous, so it is shown as-is rather than
 * guessed at. `rowIndex` is 0-based on the wire; humans count from 1.
 */
export function formatRowError(err) {
  const row = Number.isInteger(err?.rowIndex) ? `Row ${err.rowIndex + 1}` : "Row";
  const field = err?.field ? ` · ${err.field}` : "";
  const value =
    err?.rejectedValue === null || err?.rejectedValue === undefined || err.rejectedValue === ""
      ? ""
      : ` (got "${err.rejectedValue}")`;
  return `${row}${field}: ${err?.message ?? err?.code ?? "rejected"}${value}`;
}
